import jwtDecode from 'jwt-decode';
import Misc from 'src/utils/helper/misc';
import store from 'src/vuex';
import { isFeatureEnabled } from 'modules/feature-flag/index.js';
import { FEATURE_NAMES } from 'modules/feature-flag/constants.js';
import * as AppRoutesName from 'src/utils/constants/app-routes-name';
import { getNavLockLevel } from 'src/utils/helper/contractTermination';
import { NAV_LOCK_LEVEL } from 'src/utils/constants/contract-termination';

/**
 * Determines whether the user should be redirected to the Verify Email page.
 *
 * Redirect will occur only if:
 * - The user is NOT impersonating another user
 * - The user's email is NOT verified
 * - The grace period for email verification has expired
 *
 * @param {Object} store - Vuex store instance
 * @param {Object} store.getters - Vuex getters
 * @param {boolean} store.getters['auth/impersonate'] - Indicates if impersonation is active
 * @param {boolean} store.getters['auth/isEmailVerified'] - Indicates if the user's email is verified
 * @param {boolean} store.getters['auth/gracePeriodExpired'] - Indicates if the grace period has expired
 *
 * @returns {boolean} Returns true if the user should be redirected to the Verify Email page, otherwise false
 */
export function shouldRedirectToVerifyEmailPage(store) {
  if (store.getters['auth/impersonate']) {
    return false;
  }
  if (store.getters['auth/hasRole'](['foundrySupport'])) {
    return false;
  }
  const isGracePeriodExpired = store.getters['auth/isGracePeriodExpired'];
  return isGracePeriodExpired;
}

/**
 * Determines whether the user should be redirected to the Dashboard.
 *
 * Redirect will occur if:
 * - The grace period for email verification has NOT expired
 *
 * @param {Object} store - Vuex store instance
 * @param {Object} store.getters - Vuex getters
 * @param {boolean} store.getters['auth/gracePeriodExpired'] - Indicates if the grace period has expired
 *
 * @returns {boolean} Returns true if the user should be redirected to the Dashboard, otherwise false
 */
export const shouldRedirectToDashboard = (store) => {
  const isGracePeriodExpired = store.getters['auth/isGracePeriodExpired'];
  return !isGracePeriodExpired;
}

function defaultRouteCallback(to, from, next) {
  if (store.getters['auth/isUserAuthenticated']) {
    const appStatus = window.localStorage.getItem('application_status');
    if (appStatus !== 'exists') {
      if (appStatus === 'new' || appStatus === 'error') {
        return next({ name: 'get_started' });
      }
      const appStatuses = ['prohibited', 'rejected', 'in_review', 'approved'];
      if (appStatuses.includes(appStatus)) {
        return next({ name: 'application_status' });
      }
    } else {
      const userRoles = window.localStorage.getItem('roles') || '';
      const routeMap = {
        foundrySupport: 'home',
        bulkIngestionUser: 'InventoryIngestionHub',
        dropShipUser: 'DropShip'
      };
      for (const role in routeMap) {
        if (userRoles.includes(role)) {
          return next({ name: routeMap[role] });
        }
      }
      const isUserAdmin = window.localStorage.getItem('isUserAdmin') || 'none'
      const foundriesTerminated = window.localStorage.getItem('foundriesTerminated') || 'none'
      const lockLevel = getNavLockLevel({ foundriesTerminated, isUserAdmin })
      if (lockLevel === NAV_LOCK_LEVEL.FULL) {
        return next({ name: AppRoutesName.TERMINATED_CONTRACT });
      }

      if (shouldRedirectToVerifyEmailPage(store)) {
        return next({ name: AppRoutesName.VERIFY_EMAIL });
      }
      return next({ name: 'dashboard' });
    }
  } else {
    Misc.redirectToExternalCMSURL();
  }
}

function loginHandlerCallback(to, from, next) {
  const isRedirectBackToLoginFromAuthO = Misc.isValidValue(to) && to?.fullPath?.includes('access_token');
  const isRedirectBackToLoginWithToken = Misc.isValidValue(to) && to?.fullPath?.includes('authToken');

  if (isRedirectBackToLoginWithToken && to?.query?.authToken) {
    next();
    return;
  }

  if (isRedirectBackToLoginFromAuthO && to.fullPath.split('&')[4]?.split('=')[1] === 'STATE') {
    const token = to.fullPath.split('&')[0].split('=')[1];
    next({ name: 'login_page', query: { authToken: token } });
    return;
  }

  if (isRedirectBackToLoginFromAuthO) {
    const first = to.fullPath.split('&')[0];
    const stateEncoded = to.fullPath.split('&')[4]?.split('=')[1];
    const state = atob(decodeURIComponent(stateEncoded));
    const token = first.split('=')[1];
    let code = '';

    if (to.fullPath.includes('code=')) {
      code = to.fullPath.split('code=')[1];
    }

    if (state === atob(window.localStorage.getItem('jwt_token'))) {
      const decoded = jwtDecode(state);
      switch (decoded.method) {
        case 'login':
          next({ name: 'login_page', query: { authToken: token } });
          break;
        case 'payment-create':
          next({ name: 'payment_accounts', query: { method: 'create', code } });
          break;
        case 'payment-update':
          next({ name: 'payment_accounts', query: { method: 'update', code } });
          break;
        default:
          next({ name: '403' });
      }
    } else {
      next({ name: '403' });
    }
    return;
  }
  // Fallback if not redirecting
  if (window.localStorage.getItem('api_token')) {
    next();
  } else {
    Misc.redirectToExternalCMSURL();
    return next(false);
  }
}

function inventoryHubCallback(to, from, next) {
  const rolesArray = store.getters['auth/getAuthRoles'];
  if (rolesArray.includes('foundrySupport') || rolesArray.includes('bulkIngestionUser')) {
    next();
  } else {
    next({ name: '403' });
  }
}

function dropshipCallback(to, from, next) {
  const rolesArray = store.getters['auth/getAuthRoles'];
  const isRoleAccessAllowed = rolesArray.includes('foundrySupport') || rolesArray.includes('dropShipUser');
  const _isFeatureEnabled = isFeatureEnabled(FEATURE_NAMES.DROP_SHIP);
  if (isRoleAccessAllowed && _isFeatureEnabled) {
    next();
  } else {
    next({ name: '403' });
  }
}

function quarterlyEarningsUploadCallback(to, from, next) {
  const rolesArray = store.getters['auth/getAuthRoles'];
  if (rolesArray.includes('foundrySupport')) {
    next();
  } else {
    next({ name: '403' });
  }
}

export {
  defaultRouteCallback,
  loginHandlerCallback,
  inventoryHubCallback,
  dropshipCallback,
  quarterlyEarningsUploadCallback
};
